// The enumerate-repos + mint-token + per-repo seed-refresh loop, kept out of
// buildConvergeDeps' closure (platform-converge-cli.ts) so it is unit-testable
// without a live Foundry/GitHub App round-trip. Wired in as `applySeedRefresh`.

import * as fs from "node:fs";
import * as path from "node:path";
import type { TokenCredential } from "@azure/identity";
import { mintInstallationToken } from "@m8t-stack/github-app-auth";
import type { ReleaseManifest } from "./release-manifest.js";
import { resolveReleaseContent, resolveReleaseContentWithAgents } from "./release-content.js";
import type { DiscoveredAgent } from "./foundry-discovery.js";
import {
  appRepoProbe,
  probeRepoState,
  readRepoFileViaApp,
  getInitialSeedCommitSha,
  commitFilesFromBaseViaApp,
  openPullRequestViaApp,
  findOpenPr,
  updatePullRequestViaApp,
  closePullRequestViaApp,
} from "./github-app-repo.js";
import { parseSeedManifest, deriveOwnedPaths, SEED_MANIFEST_PATH, type SeedLineage } from "./seed-manifest.js";
import { refreshBrainRepo, type RepoRefreshApi, type SeedTrees, type RepoRefreshResult } from "./seed-refresh.js";

/** Maps a deployed agent's `metadata.persona` to the seed lineage name it carries.
 *  The only two v1 seeds — extend here when a new built-in seed ships. */
export const PERSONA_SEED_MAP: Record<string, string> = {
  "startup-advisor": "startup-advisor",
  "ezra": "ezra",
};

const ADVISOR_LABEL: Record<string, string> = {
  "startup-advisor": "Startup Advisor",
  "ezra": "Azure Expert",
};

export interface BrainRepoRef {
  repo: string;
  seedName: string | null;
  installationId: string;
}

/** Unique brain repos across deployed agents; seedName inferred from the agent's persona.
 *  Agents with no/unparsable `metadata.brain`, no `repo` field, or no `installationId`
 *  field are skipped — both are required to mint a scoped GitHub App token for the repo. */
export function dedupeBrainRepos(agents: DiscoveredAgent[]): BrainRepoRef[] {
  const byRepo = new Map<string, BrainRepoRef>();
  for (const a of agents) {
    const raw = a.metadata.brain;
    if (!raw) continue;
    let repo: string | undefined;
    let installationId: string | undefined;
    try {
      const parsed = JSON.parse(raw) as { repo?: string; installationId?: string };
      repo = parsed.repo;
      installationId = parsed.installationId;
    } catch {
      repo = undefined;
      installationId = undefined;
    }
    if (!repo || !installationId) continue;
    const seedName = a.metadata.persona ? (PERSONA_SEED_MAP[a.metadata.persona] ?? null) : null;
    if (!byRepo.has(repo)) byRepo.set(repo, { repo, seedName, installationId });
  }
  return [...byRepo.values()];
}

/**
 * Scope a repo-root content directory down to one seed's subtree, using the
 * manifest's `brainSeeds.items[seedName].path` (e.g. `brain-seeds/startup-advisor`).
 * Returns null when the seed isn't part of this release — the caller skips that repo.
 * CRITICAL: without this, `deriveOwnedPaths(contentRoot)` walks the tarball's REPO-ROOT
 * skills/references/targets — where the platform's own `targets/` (CI build actions)
 * live and the seed content does NOT — delivering the wrong files entirely.
 */
export function seedContentDir(contentRoot: string, manifest: ReleaseManifest, seedName: string): string | null {
  // Indexed access into a Record is typed as always-present (no noUncheckedIndexedAccess),
  // but a seed genuinely absent from this release is real at runtime — check ownership.
  const items = manifest.components.brainSeeds.items;
  if (!Object.hasOwn(items, seedName)) return null;
  const dir = path.join(contentRoot, items[seedName].path);
  // The manifest saying a seed exists is not the same as this content root having it.
  // A historical platform commit resolved as a merge base predates the extraction —
  // or postdates it — and either way may not carry the seed the manifest names. A
  // truthy path to a missing directory reads downstream as "an empty base", which
  // fabricates a lineage recording a revision that never held this content.
  return fs.existsSync(dir) ? dir : null;
}

/** Bind a minted per-repo token into the `RepoRefreshApi` surface `refreshBrainRepo` expects. */
function bindRepoApi(token: string, repo: string): RepoRefreshApi {
  return {
    getDefaultBranch: () => Promise.resolve("main"),
    getHeadSha: async (branch) => {
      // Reuse the Git Data ref read via a tiny fetch (kept here to avoid widening
      // github-app-repo's surface further for a single-field read).
      const res = await fetch(`https://api.github.com/repos/${repo}/git/ref/heads/${branch}`, {
        headers: { Authorization: `token ${token}`, Accept: "application/vnd.github+json", "User-Agent": "m8t" },
      });
      const j = (await res.json()) as { object?: { sha?: string } };
      return j.object?.sha ?? "";
    },
    findOpenPr: (head) => findOpenPr({ token, repo, head }),
    commitFromBase: (files, branch, baseSha, message) => commitFilesFromBaseViaApp({ token, repo, branch, baseSha, files, message }),
    openPr: (head, base, title, body) => openPullRequestViaApp({ token, repo, base, head, title, body }),
    updatePr: (n, title, body) => updatePullRequestViaApp({ token, repo, number: n, title, body }),
    closePr: (n, comment) => closePullRequestViaApp({ token, repo, number: n, comment }),
    readFileAtRef: (path, ref) => readRepoFileViaApp({ token, repo, path, ref }),
  };
}

export interface RunSeedRefreshArgs {
  credential: TokenCredential;
  kvUri: string;
  manifest: ReleaseManifest;
  agents: DiscoveredAgent[];
  version: string;
  onProgress?: (m: string) => void;
  onWarn?: (m: string) => void;
}

export interface RunSeedRefreshResult {
  delivered: number;
  noop: number;
  failed: number;
  results: RepoRefreshResult[];
}

/**
 * Enumerate the install's unique brain repos, mint a scoped GitHub App token per
 * repo, resolve each repo's seed lineage (marker → bootstrap → re-adopt), and run
 * `refreshBrainRepo`. Per-repo work is fully isolated: the two SOFT-skip cases
 * (repo isn't a reachable m8t brain; seed lineage unresolvable) `continue`
 * without counting as a failure, and every THROWING step for a given repo —
 * minting its token, probing/reading it, and `refreshBrainRepo` itself — is
 * caught, warned via `onWarn`, and counted in `failed`. A single broken brain
 * repo therefore can never abort the whole converge; the caller (`applySeedRefresh`
 * in platform-converge-cli.ts) surfaces a non-zero `failed` count as a warning.
 */
export async function runSeedRefresh(args: RunSeedRefreshArgs): Promise<RunSeedRefreshResult> {
  const targetCommit = args.manifest.platform.commit;
  // Agent-sourced seeds live in their own repos, so "the content of this release"
  // is the platform tarball PLUS the agents this manifest pinned. Without the
  // hydration an extracted seed resolves to nothing and every refresh for it
  // becomes a silent no-op.
  const theirsRoot = await resolveReleaseContentWithAgents(args.manifest, {});
  const repos = dedupeBrainRepos(args.agents);
  const results: RepoRefreshResult[] = [];
  let delivered = 0;
  let noop = 0;
  let failed = 0;

  for (const ref of repos) {
    try {
      const minted = await mintInstallationToken({
        credential: args.credential,
        kvUri: args.kvUri,
        installationId: ref.installationId,
        repository: ref.repo,
        permissions: { contents: "write", pull_requests: "write" },
      });
      const token = minted.token;
      const probe = appRepoProbe({ token, repo: ref.repo });
      const state = await probeRepoState(probe);
      if (state !== "m8t-brain") {
        args.onWarn?.(`${ref.repo}: not a reachable m8t brain (${state}) — skipped.`);
        continue;
      }

      // Resolve the lineage marker (or bootstrap).
      const markerText = await readRepoFileViaApp({ token, repo: ref.repo, path: SEED_MANIFEST_PATH });
      const marker = parseSeedManifest(markerText);
      const seedName = ref.seedName ?? marker?.seeds[0]?.name ?? null;
      if (!seedName) {
        args.onWarn?.(`${ref.repo}: cannot determine seed lineage — skipped.`);
        continue;
      }
      // Scope both content trees to this seed's subtree (brain-seeds/<seedName>/…); the
      // tarball root holds the platform's OWN skills/references/targets, not the seed's.
      const theirsDir = seedContentDir(theirsRoot, args.manifest, seedName);
      if (!theirsDir) {
        args.onWarn?.(`${ref.repo}: seed '${seedName}' is not in this release — skipped.`);
        continue;
      }
      const advisorLabel = ADVISOR_LABEL[seedName] ?? seedName;

      let lineage: SeedLineage | null = marker?.seeds.find((s) => s.name === seedName) ?? null;
      let baseDir: string | null = null;
      // A seed marker records a PLATFORM commit, which cannot address an agent repo's
      // history. For a seed that pre-dates the extraction the platform tarball at that
      // commit still carries it, so a genuine 3-way merge is possible and is used. Once
      // the recorded revision post-dates the extraction there is nothing to resolve, and
      // `seedContentDir` now returns null rather than a path to a missing directory —
      // so the refresh proceeds without a base.
      //
      // Without a base the merge classifier treats every file as an upstream ADD: new
      // files are delivered, files the founder already has are SKIPPED, nothing is
      // overwritten. Safe, and less useful than a real merge. Restoring it means
      // recording agent provenance in the seed marker's lineage — named debt.
      // The merge BASE is resolved by platform commit, which cannot address agent
      // content: an agent repo's history is its own, and the seed marker records
      // only a platform sha. So an agent-sourced seed has no reachable base and
      // refreshes without one.
      //
      // That is safe, not silent-lossy: with a null base the merge classifier
      // treats every file as an upstream ADD, which delivers files the founder
      // does not have and SKIPS ("you already have a file at this path") the ones
      // they do. It never clobbers. It is simply less useful than a real 3-way
      // merge, and it is announced rather than inferred from a fetch failure.
      //
      // Restoring the 3-way merge means recording agent provenance in the seed
      // marker's lineage. Named debt, not an accident.
      const agentSourced = Boolean(args.manifest.components.agents &&
        Object.values(args.manifest.components.agents).some((a) => a.persona === seedName));
      if (lineage) {
        try {
          const baseRoot = await resolveReleaseContent(
            { ...args.manifest, platform: { ...args.manifest.platform, commit: lineage.refreshedToCommit } },
            {},
          );
          baseDir = seedContentDir(baseRoot, args.manifest, seedName);
        } catch {
          baseDir = null;
          args.onWarn?.(`${ref.repo}: base rev ${lineage.refreshedToCommit} unfetchable — falling back to marker-only re-adopt.`);
          lineage = null;
        }
      } else {
        const initSha = await getInitialSeedCommitSha({ token, repo: ref.repo });
        if (initSha) {
          try {
            const baseRoot = await resolveReleaseContent(
              { ...args.manifest, platform: { ...args.manifest.platform, commit: initSha } },
              {},
            );
            baseDir = seedContentDir(baseRoot, args.manifest, seedName);
            lineage = baseDir
              ? { name: seedName, refreshedToCommit: initSha, refreshedToVersion: "(bootstrap)", ownedPaths: deriveOwnedPaths(baseDir) }
              : null;
          } catch {
            baseDir = null;
          }
        }
      }

      // Warn only where it is TRUE — after both branches have had their chance. An
      // agent-sourced seed CAN still resolve a base (a brain seeded before the
      // extraction: the platform tarball at that commit still carries it), so warning
      // up front told founders they were losing a merge they were about to get.
      if (agentSourced && !baseDir) {
        args.onWarn?.(
          `${ref.repo}: seed '${seedName}' comes from an agent repo and no merge base could be resolved ` +
          "— new files are delivered, files you already have are skipped and never overwritten.",
        );
      }

      const trees: SeedTrees = { baseDir, theirsDir, ownedAtTheirs: deriveOwnedPaths(theirsDir) };
      const source = { readOurs: (p: string) => readRepoFileViaApp({ token, repo: ref.repo, path: p }) };
      // priorManifest carries the FULL on-disk marker (all sibling seed lineages),
      // not just the one lineage being refreshed here — refreshBrainRepo's upsert
      // must preserve siblings rather than clobber the marker down to one entry.
      const res = await refreshBrainRepo({
        repo: ref.repo,
        branch: `m8t/seed-update-${args.version}`,
        base: "main",
        version: args.version,
        notesUrl: args.manifest.platform.notesUrl,
        advisorLabel,
        seedName,
        lineage,
        trees,
        source,
        api: bindRepoApi(token, ref.repo),
        targetCommit,
        priorManifest: marker,
      });
      results.push(res);
      if (res.action === "noop") noop++;
      else delivered++;
      args.onProgress?.(`${ref.repo}: ${res.action}${res.prUrl ? ` → ${res.prUrl}` : ""}`);
    } catch (e) {
      failed++;
      args.onWarn?.(`${ref.repo}: seed refresh failed — ${(e as Error).message}; skipped this repo.`);
      continue;
    }
  }
  return { delivered, noop, failed, results };
}
